import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Stack,
  Chip,
  Button,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  TableHead,
  Container,
  Typography,
  IconButton,
  TableContainer,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  InputAdornment,
} from '@mui/material';
import {
  collection,
  getDocs,
  getFirestore,
  deleteDoc,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import { TableSkeleton } from '../components/loading';
import firebaseApp from '../utils/firebaseConfig';

const db = getFirestore(firebaseApp);

const emptyForm = {
  code: '',
  description: '',
  discountType: 'percent', // 'percent' | 'fixed'
  discountValue: '',
  validFrom: '',
  validUntil: '',
  categoryIds: [],
  maxUses: '',
  active: true,
};

export default function CouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [row, setRow] = useState({});

  // Dialog crear/editar
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setTableLoading(true);
    try {
      const [couponsSnap, catsSnap] = await Promise.all([
        getDocs(collection(db, 'coupons')),
        getDocs(collection(db, 'reservation_categories')),
      ]);
      setCoupons(couponsSnap.docs.map((d) => ({ ...d.data(), id: d.id })));
      setCategories(catsSnap.docs.map((d) => ({ ...d.data(), id: d.id })).filter((c) => c.active !== false));
    } catch (error) {
      console.error('Error fetching coupons:', error);
    }
    setTableLoading(false);
  };

  const getCategoryName = (catId) => {
    const cat = categories.find((c) => c.id === catId);
    return cat ? cat.name : catId;
  };

  const handleOpenMenu = (event, r) => { setOpen(event.currentTarget); setRow(r); };
  const handleCloseMenu = () => setOpen(null);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = () => {
    setEditingId(row.id);
    setForm({
      code: row.code || '',
      description: row.description || '',
      discountType: row.discountType || 'percent',
      discountValue: row.discountType === 'fixed'
        ? String((row.discountValue || 0) / 100)
        : String(row.discountValue || ''),
      validFrom: row.validFrom || '',
      validUntil: row.validUntil || '',
      categoryIds: row.categoryIds || [],
      maxUses: row.maxUses ? String(row.maxUses) : '',
      active: row.active !== false,
    });
    setFormError('');
    setDialogOpen(true);
    handleCloseMenu();
  };

  const handleDelete = async () => {
    if (!window.confirm(`¿Seguro que quieres borrar el cupón "${row.code}"?`)) return;
    try {
      await deleteDoc(doc(db, 'coupons', row.id));
      setCoupons(coupons.filter((c) => c.id !== row.id));
    } catch (error) {
      alert('Error al borrar el cupón');
    }
    handleCloseMenu();
  };

  const handleToggleActive = async (item) => {
    try {
      await updateDoc(doc(db, 'coupons', item.id), { active: !item.active });
      setCoupons(coupons.map((c) => (c.id === item.id ? { ...c, active: !c.active } : c)));
    } catch (error) {
      console.error('Error toggling active:', error);
    }
  };

  const handleSave = async () => {
    setFormError('');
    const code = form.code.trim().toUpperCase();
    if (!code) { setFormError('Ingresa el código del cupón.'); return; }
    const value = Number(form.discountValue);
    if (!value || value <= 0) { setFormError('Ingresa el valor del descuento.'); return; }
    if (form.discountType === 'percent' && value > 100) { setFormError('El porciento no puede ser mayor de 100.'); return; }
    if (form.validFrom && form.validUntil && form.validFrom > form.validUntil) {
      setFormError('La fecha "válido desde" no puede ser después de "válido hasta".');
      return;
    }
    // Código duplicado (en otro cupón)
    const dup = coupons.find((c) => (c.code || '').toUpperCase() === code && c.id !== editingId);
    if (dup) { setFormError('Ya existe un cupón con ese código.'); return; }

    setSaving(true);
    try {
      const data = {
        code,
        description: form.description.trim(),
        discountType: form.discountType,
        // Los montos fijos se guardan en centavos (igual que los precios)
        discountValue: form.discountType === 'fixed' ? Math.round(value * 100) : Math.round(value),
        validFrom: form.validFrom || null,
        validUntil: form.validUntil || null,
        categoryIds: form.categoryIds,
        maxUses: form.maxUses ? Number(form.maxUses) : null,
        active: form.active,
      };
      if (editingId) {
        await updateDoc(doc(db, 'coupons', editingId), data);
      } else {
        await addDoc(collection(db, 'coupons'), { ...data, usedCount: 0, createdAt: serverTimestamp() });
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error(error);
      setFormError('Error al guardar el cupón.');
    }
    setSaving(false);
  };

  const toggleCategory = (catId) => {
    setForm((f) => ({
      ...f,
      categoryIds: f.categoryIds.includes(catId)
        ? f.categoryIds.filter((id) => id !== catId)
        : [...f.categoryIds, catId],
    }));
  };

  const formatDiscount = (c) => (c.discountType === 'percent'
    ? `${c.discountValue}%`
    : `$${((c.discountValue || 0) / 100).toFixed(2)}`);

  const formatValidity = (c) => {
    if (!c.validFrom && !c.validUntil) return 'Siempre';
    if (c.validFrom && c.validUntil) return `${c.validFrom} → ${c.validUntil}`;
    if (c.validFrom) return `Desde ${c.validFrom}`;
    return `Hasta ${c.validUntil}`;
  };

  const todayIso = new Date().toISOString().split('T')[0];
  const isExpired = (c) => c.validUntil && todayIso > c.validUntil;

  return (
    <>
      <Helmet>
        <title>Cupones | BBTC Dashboard</title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>Cupones</Typography>
          <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={openCreate}>
            Nuevo Cupón
          </Button>
        </Stack>

        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Descuento</TableCell>
                    <TableCell>Vigencia</TableCell>
                    <TableCell>Categorías</TableCell>
                    <TableCell>Usos</TableCell>
                    <TableCell>Activo</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableLoading && <TableSkeleton rows={4} columns={7} />}
                  {!tableLoading && coupons.map((c) => (
                    <TableRow hover key={c.id}>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="subtitle2" sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>{c.code}</Typography>
                          {isExpired(c) && <Chip label="Expirado" size="small" sx={{ bgcolor: '#F5F5F5', color: '#9e9e9e' }} />}
                        </Stack>
                        {c.description && (
                          <Typography variant="caption" color="text.secondary">{c.description}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" color="#d40100">{formatDiscount(c)}</Typography>
                      </TableCell>
                      <TableCell>{formatValidity(c)}</TableCell>
                      <TableCell>
                        {(c.categoryIds || []).length === 0 ? (
                          <Chip label="Todas" size="small" variant="outlined" />
                        ) : (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                            {c.categoryIds.map((catId) => (
                              <Chip key={catId} label={getCategoryName(catId)} size="small" variant="outlined" />
                            ))}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell>
                        {c.usedCount || 0}{c.maxUses ? ` / ${c.maxUses}` : ''}
                      </TableCell>
                      <TableCell>
                        <Switch checked={c.active || false} onChange={() => handleToggleActive(c)} />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="large" color="inherit" onClick={(e) => handleOpenMenu(e, c)}>
                          <Iconify icon="eva:more-vertical-fill" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!tableLoading && coupons.length === 0 && (
                    <TableRow>
                      <TableCell align="center" colSpan={7} sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No hay cupones. Crea uno para tus especiales.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { p: 1, width: 140 } }}
      >
        <MenuItem onClick={openEdit}>
          <Iconify icon="eva:edit-fill" sx={{ mr: 2 }} /> Editar
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <Iconify icon="eva:trash-2-outline" sx={{ mr: 2 }} /> Borrar
        </MenuItem>
      </Popover>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Editar Cupón' : 'Nuevo Cupón'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField
              label="Código"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              fullWidth
              required
              placeholder="ej. VISITA10"
              inputProps={{ style: { textTransform: 'uppercase', fontFamily: 'monospace', letterSpacing: 1 } }}
              helperText="El código que el cliente escribe en el checkout"
            />
            <TextField
              label="Descripción (interna)"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
              placeholder="ej. Especial visitas agosto"
            />
            <Stack direction="row" spacing={2} alignItems="center">
              <RadioGroup
                row
                value={form.discountType}
                onChange={(e) => setForm({ ...form, discountType: e.target.value })}
              >
                <FormControlLabel value="percent" control={<Radio size="small" />} label="Porciento (%)" />
                <FormControlLabel value="fixed" control={<Radio size="small" />} label="Monto fijo ($)" />
              </RadioGroup>
            </Stack>
            <TextField
              label={form.discountType === 'percent' ? 'Porciento de descuento' : 'Monto de descuento'}
              value={form.discountValue}
              onChange={(e) => setForm({ ...form, discountValue: e.target.value.replace(/[^\d.]/g, '') })}
              fullWidth
              required
              InputProps={{
                startAdornment: form.discountType === 'fixed' ? <InputAdornment position="start">$</InputAdornment> : null,
                endAdornment: form.discountType === 'percent' ? <InputAdornment position="end">%</InputAdornment> : null,
              }}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Válido desde"
                type="date"
                value={form.validFrom}
                onChange={(e) => setForm({ ...form, validFrom: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Vacío = sin fecha de inicio"
              />
              <TextField
                label="Válido hasta"
                type="date"
                value={form.validUntil}
                onChange={(e) => setForm({ ...form, validUntil: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Vacío = no expira"
              />
            </Stack>
            <TextField
              label="Límite de usos (opcional)"
              value={form.maxUses}
              onChange={(e) => setForm({ ...form, maxUses: e.target.value.replace(/\D/g, '') })}
              fullWidth
              helperText="Vacío = sin límite"
            />
            <Typography variant="subtitle2">Categorías donde aplica</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: '0 !important' }}>
              Si no marcas ninguna, el cupón aplica a TODAS las categorías.
            </Typography>
            <Stack spacing={0.5} sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1 }}>
              {categories.map((cat) => (
                <FormControlLabel
                  key={cat.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={form.categoryIds.includes(cat.id)}
                      onChange={() => toggleCategory(cat.id)}
                    />
                  }
                  label={<Typography variant="body2">{cat.name}</Typography>}
                />
              ))}
            </Stack>
            <FormControlLabel
              control={<Switch checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />}
              label="Activo"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar Cupón'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
